import moment from 'jalali-moment'
import { DigitToMonth } from 'utilities/helper/digitToMonth'
import { fundTitle } from 'utilities/helper/format'
import { MoneyPoolTransactionTypes } from 'utilities/helper/moneyPools'

// «تراکنش‌های من»: every payment the member was on either end of, from every
// box they are in, wallet top-ups and plan upgrades included. One row is one
// `payment.Transaction`, so the words and the direction below are worked out
// from where the money went (`source` → `destination`) and whether the member
// was the one paying — the row itself carries no «in» or «out».

/**
 * Which way the money went, as the member would say it.
 *
 *  - `in`:         it reached their own bank account (a loan, a withdrawal).
 *  - `out`:        they paid it (an installment, a membership fee, an upgrade).
 *  - `wallet_in`:  «شارژ کیف پول» — money arrived in their Hamyan wallet.
 *  - `wallet_out`: «برداشت از کیف پول» — it left the wallet for their own bank
 *                  account. What leaves the wallet *for a box* is `out`: the
 *                  box is what the member is looking for on that row, and the
 *                  wallet is only how it got there (`hint`).
 *  - `move`:       it moved without leaving or reaching their pocket — a manager
 *                  paying another member out of the box, somebody else paying an
 *                  installment on their behalf, a box settling to its own account.
 */
export type TMyTransactionKind =
    | 'in'
    | 'out'
    | 'wallet_in'
    | 'wallet_out'
    | 'move'

/** `payment.Transaction.source` / `.destination` — `utils/constants/choice.py` */
const GATEWAY = 'gtw'
const WALLET = 'hwt'
const HAMYAN_PLAN = 'hpl'
const BOX_BALANCE = 'hbb'
const BOX_BANK_ACCOUNT = 'bba'
const MEMBER_BANK_ACCOUNT = 'mba'
const CASH = 'csh'

/** `payment.Transaction.ctx_type` */
const CTX_MONEYPOOL = 'mnp'
const CTX_CYCLE = 'ccl'
const CTX_ORDER = 'ord'

const BOX_SIDES = [BOX_BALANCE, BOX_BANK_ACCOUNT]

const LOAN = 'ctl'
const CUSTOM_CASHOUT = 'ctc'

export function kindOf(
    item: IDashboardTransactionResult,
    me: number | null
): TMyTransactionKind {
    const { source, destination } = item
    if (destination === WALLET) return 'wallet_in'
    if (destination === MEMBER_BANK_ACCOUNT) {
        // The manager who pays a loan out is the payer of that row too, so
        // «I paid» says nothing here: only the member it reached received it.
        if (item.receiver !== me) return 'move'
        // Out of their own wallet into their own bank account: nothing was
        // earned or spent, the same money changed pocket.
        return source === WALLET ? 'wallet_out' : 'in'
    }
    if (BOX_SIDES.includes(source) && BOX_SIDES.includes(destination))
        return 'move'
    if (destination === HAMYAN_PLAN) return 'out'
    if (BOX_SIDES.includes(destination))
        return item.payer === me ? 'out' : 'move'
    if (item.payer === me) return 'out'
    if (item.receiver === me) return 'in'
    return 'move'
}

/** «+» / «−», or nothing for a row that never touched the member's pocket. */
export function signOf(kind: TMyTransactionKind): '+' | '−' | '' {
    if (kind === 'in' || kind === 'wallet_in') return '+'
    if (kind === 'out' || kind === 'wallet_out') return '−'
    return ''
}

/** How the money travelled, drawn as a small mark beside the words for it. */
export type THintIcon = 'wallet' | 'gateway' | 'cash' | 'bank' | 'link'

export interface IMyTransactionHint {
    icon: THintIcon
    /** «برداشت از کیف پول», «با درگاه بانکی», «برای پرداخت به صندوق مهر» */
    label: string
}

export interface IMyTransactionView {
    kind: TMyTransactionKind
    /** «صندوق مهر», «کیف پول همیان», «ارتقای صندوق» */
    title: string
    /** what it was for: «پرداخت قسط», «شارژ کیف پول», «دریافت وام» */
    subtitle: string
    /** the other person in it, when there is one: «برای …», «به …», «پرداخت توسط …» */
    party: string
    /** which account it came out of, or what a top-up was spent on */
    hint: IMyTransactionHint | null
    /** the box's own page, when the row says which box */
    boxPath: string | null
}

const trimmed = (name: string | null | undefined) => (name ?? '').trim()

const toman = (amount: number) => `${amount.toLocaleString()} تومان`

/** «صندوق مهر» / «ارتقای صندوق» — what a row was for, without its links. */
function targetName(item: IDashboardTransactionResult): string {
    if (item.ctx_type === CTX_ORDER || item.destination === HAMYAN_PLAN)
        return 'ارتقای صندوق'
    if (item.moneypool_data)
        return fundTitle(item.moneypool_data.moneypool_name)
    if (item.cycle_data) return fundTitle(item.cycle_data.cashbox_name)
    return 'همیان'
}

function moneypoolSubtitle(
    item: IDashboardTransactionResult,
    kind: TMyTransactionKind
): string {
    const type = item.moneypool_data?.type ?? ''
    if (kind === 'in') {
        if (type === LOAN) return 'دریافت وام'
        if (type === CUSTOM_CASHOUT) return 'برداشت از صندوق'
    }
    if (type && MoneyPoolTransactionTypes[type])
        return MoneyPoolTransactionTypes[type]
    // No cash-in or cash-out row behind it: the box's own money moving from
    // «موجودی نزد همیان» to the box's bank account.
    if (BOX_SIDES.includes(item.source) && BOX_SIDES.includes(item.destination))
        return 'انتقال به حساب صندوق'
    return item.moneypool_data?.is_cashin ? 'واریز به صندوق' : 'برداشت از صندوق'
}

function cycleSubtitle(
    item: IDashboardTransactionResult,
    kind: TMyTransactionKind
): string {
    if (item.destination === MEMBER_BANK_ACCOUNT)
        return kind === 'in' ? 'دریافت وام قرعه‌کشی' : 'پرداخت وام قرعه‌کشی'
    if (BOX_SIDES.includes(item.source) && BOX_SIDES.includes(item.destination))
        return 'انتقال به حساب صندوق'
    return 'پرداخت سهم'
}

/** «شارژ کیف پول» / «برگشت پول به کیف پول» — the two ways money gets in. */
function walletInSubtitle(item: IDashboardTransactionResult): string {
    if (item.source === GATEWAY) return 'شارژ کیف پول'
    if (BOX_SIDES.includes(item.source)) return 'برگشت پول به کیف پول'
    return 'واریز به کیف پول'
}

function partyOf(
    item: IDashboardTransactionResult,
    kind: TMyTransactionKind,
    me: number | null
): string {
    const receiver = trimmed(item.receiver_name)
    const payer = trimmed(item.payer_name)
    if (item.destination === MEMBER_BANK_ACCOUNT) {
        return kind === 'move' && receiver ? `به ${receiver}` : ''
    }
    if (!BOX_SIDES.includes(item.destination)) return ''
    if (BOX_SIDES.includes(item.source)) return ''
    // Paid for somebody else, or by somebody else. The one who pays for a
    // whole family is exactly who needs to tell these rows apart.
    if (kind === 'out' && item.receiver !== null && item.receiver !== me)
        return receiver ? `برای ${receiver}` : ''
    if (kind === 'move' && item.payer !== me)
        return payer ? `پرداخت توسط ${payer}` : ''
    return ''
}

/**
 * «برای پرداخت به صندوق مهر» — what a top-up was handed straight on to. Said
 * on the top-up row so the «+» and the «−» under it read as one payment.
 */
function spentOnLabel(spent: NonNullable<IWalletLink['spentOn']>): string {
    return spent.count > 1
        ? `برای ${spent.count} پرداخت در همان لحظه`
        : `برای پرداخت به ${spent.titles[0]}`
}

function hintOf(
    item: IDashboardTransactionResult,
    kind: TMyTransactionKind,
    me: number | null,
    link?: IWalletLink
): IMyTransactionHint | null {
    if (kind === 'wallet_in') {
        if (link?.spentOn)
            return { icon: 'link', label: spentOnLabel(link.spentOn) }
        if (item.source === GATEWAY)
            return { icon: 'gateway', label: 'با درگاه بانکی' }
        if (BOX_SIDES.includes(item.source))
            return { icon: 'bank', label: 'از موجودی صندوق' }
        return null
    }
    if (kind === 'out') {
        // Everything a member pays runs through the wallet, so «برداشت از کیف
        // پول» is the one line that answers «this money came from where?».
        if (item.source === WALLET && item.payer === me)
            return { icon: 'wallet', label: 'برداشت از کیف پول' }
        if (item.source === GATEWAY)
            return { icon: 'gateway', label: 'با درگاه بانکی' }
        if (item.source === CASH)
            return { icon: 'cash', label: 'پرداخت نقدی، ثبت مدیر' }
        return null
    }
    if (kind === 'in' || kind === 'wallet_out')
        return { icon: 'bank', label: 'به حساب بانکی شما' }
    return null
}

export function describeTransaction(
    item: IDashboardTransactionResult,
    me: number | null,
    link?: IWalletLink
): IMyTransactionView {
    const kind = kindOf(item, me)
    const party = partyOf(item, kind, me)
    const hint = hintOf(item, kind, me, link)

    if (kind === 'wallet_in' || kind === 'wallet_out') {
        return {
            kind,
            title: 'کیف پول همیان',
            subtitle:
                kind === 'wallet_out'
                    ? 'برداشت از کیف پول'
                    : walletInSubtitle(item),
            party: '',
            hint,
            boxPath: null,
        }
    }
    if (item.ctx_type === CTX_ORDER || item.destination === HAMYAN_PLAN) {
        return {
            kind,
            title: 'ارتقای صندوق',
            subtitle: 'خرید اشتراک صندوق',
            party: '',
            hint,
            boxPath: null,
        }
    }
    if (item.ctx_type === CTX_MONEYPOOL && item.moneypool_data) {
        return {
            kind,
            title: fundTitle(item.moneypool_data.moneypool_name),
            subtitle: moneypoolSubtitle(item, kind),
            party,
            hint,
            boxPath: `/fund/${item.moneypool_data.id}`,
        }
    }
    if (item.ctx_type === CTX_CYCLE && item.cycle_data) {
        const cashboxId = item.cycle_data.cashbox_id
        return {
            kind,
            title: fundTitle(item.cycle_data.cashbox_name),
            subtitle: cycleSubtitle(item, kind),
            party,
            hint,
            // Only a server that sends the cashbox's id can link to it — a
            // cycle's own id is not a page anyone can open.
            boxPath:
                typeof cashboxId === 'number' ? `/cashbox/${cashboxId}` : null,
        }
    }
    return {
        kind,
        title: 'تراکنش همیان',
        subtitle: kind === 'in' ? 'دریافت' : kind === 'out' ? 'پرداخت' : '',
        party,
        hint,
        boxPath: null,
    }
}

/** Whether the row belongs to a صندوق قرعه‌کشی rather than a قرض‌الحسنه. */
export const isCashboxRow = (item: IDashboardTransactionResult) =>
    item.ctx_type === CTX_CYCLE

/**
 * «از» / «به» on the details sheet, in the words the rest of the app uses for
 * the same places.
 */
export function placeLabel(
    place: string,
    item: IDashboardTransactionResult,
    me: number | null,
    side: 'from' | 'to'
): string {
    switch (place) {
        case GATEWAY:
            return 'درگاه بانکی'
        case WALLET:
            return 'کیف پول همیان'
        case BOX_BALANCE:
            return 'موجودی صندوق نزد همیان'
        case BOX_BANK_ACCOUNT:
            return 'حساب بانکی صندوق'
        case HAMYAN_PLAN:
            return 'همیان'
        case CASH:
            return 'نقدی'
        case MEMBER_BANK_ACCOUNT: {
            const owner = side === 'to' ? item.receiver : item.payer
            const name = trimmed(
                side === 'to' ? item.receiver_name : item.payer_name
            )
            if (owner === me) return 'حساب بانکی شما'
            return name ? `حساب بانکی ${name}` : 'حساب بانکی عضو'
        }
        default:
            return ''
    }
}

/**
 * What left the member's pocket: the amount and the commission charged on
 * top of it. Only for what they paid themselves — the commission on a loan
 * paid out to them is the box's, not theirs.
 */
export function chargedTotal(item: IDashboardTransactionResult): number {
    return (item.amount ?? 0) + (item.total_commission ?? 0)
}

// --- the wallet as a corridor -------------------------------------------------

// Paying a box by gateway is booked as two rows, not one: `GatewayPayment.
// make_successful` first moves the whole basket into the member's wallet
// (`gtw → hwt`), then each item out of it (`hwt → hbb` / `bba` / `hpl`). The
// member asked for neither — they tapped «پرداخت» once — and what they see is
// a «+۵,۰۰۰» right next to a «−۵,۰۰۰», which reads like being charged twice.
//
// On 7 days of production: 1,054 of 1,152 wallet payments have a top-up right
// before them, and for 598 of 742 top-ups the payments under it add up to
// exactly the top-up. So the pair can be spotted here and said out loud.

/** How close a payment has to follow a top-up to be the same basket. */
const PASS_THROUGH_WINDOW_SECONDS = 180

export interface IWalletLink {
    /** the top-up this payment was handed straight out of */
    fundedBy?: { pk: number; amount: number }
    /** the payments this top-up was handed straight on to */
    spentOn?: { titles: string[]; count: number; total: number }
}

const isTopUp = (item: IDashboardTransactionResult, me: number | null) =>
    item.source === GATEWAY &&
    item.destination === WALLET &&
    (item.payer === me || item.receiver === me)

const isPaidFromWallet = (
    item: IDashboardTransactionResult,
    me: number | null
) => item.source === WALLET && item.payer === me && kindOf(item, me) === 'out'

/**
 * Which rows are the two halves of one payment. The list is newest first, so
 * the payments of a basket sit *above* the top-up that funded them; a top-up
 * takes the payments right after it whose totals still fit inside it, and each
 * payment is claimed once.
 */
export function linkWalletRows(
    rows: IDashboardTransactionResult[],
    me: number | null
): Map<number, IWalletLink> {
    const links = new Map<number, IWalletLink>()
    if (me === null) return links

    interface IPending {
        row: IDashboardTransactionResult
        at: number
        total: number
    }
    let pending: IPending[] = []

    rows.forEach((row) => {
        const at = tehranMoment(row)?.valueOf()
        if (at === undefined) return

        if (isPaidFromWallet(row, me)) {
            pending.push({ row, at, total: chargedTotal(row) })
            return
        }
        if (!isTopUp(row, me)) return

        const amount = row.amount ?? 0
        const taken: IPending[] = []
        let total = 0
        pending.forEach((candidate) => {
            // A payment out of the basket is booked after the top-up, within
            // seconds of it, and the basket cannot cost more than was put in.
            if (candidate.row.pk <= row.pk) return
            const gap = (candidate.at - at) / 1000
            if (gap < 0 || gap > PASS_THROUGH_WINDOW_SECONDS) return
            if (total + candidate.total > amount) return
            total += candidate.total
            taken.push(candidate)
        })
        if (!taken.length) return

        const titles: string[] = []
        taken.forEach((candidate) => {
            links.set(candidate.row.pk, {
                ...links.get(candidate.row.pk),
                fundedBy: { pk: row.pk, amount },
            })
            const title = targetName(candidate.row)
            if (!titles.includes(title)) titles.push(title)
        })
        pending = pending.filter((candidate) => !taken.includes(candidate))
        links.set(row.pk, {
            ...links.get(row.pk),
            spentOn: { titles, count: taken.length, total },
        })
    })

    return links
}

// --- «این تراکنش چه بود؟» -----------------------------------------------------

/**
 * The row said in full sentences, starting from the signed amount itself —
 * «−۵,۰۰۰ تومان یعنی …». Nothing in a ledger is self-evident to the person
 * whose money it is, and the wallet corridor least of all.
 */
export function explainTransaction(
    item: IDashboardTransactionResult,
    view: IMyTransactionView,
    me: number | null,
    link?: IWalletLink
): string {
    const amount = item.amount ?? 0
    const commission = item.total_commission ?? 0
    const lead = `«${signOf(view.kind)}${amount.toLocaleString()} تومان» یعنی`
    const box = view.title
    const paidFor = view.subtitle ? `بابت «${view.subtitle}» ` : ''

    if (view.kind === 'wallet_in') {
        const cameFrom =
            item.source === GATEWAY
                ? `${toman(
                      amount
                  )} با درگاه بانکی از حساب شما به کیف پول همیان آمد`
                : `${toman(amount)} به کیف پول همیان شما اضافه شد`
        if (link?.spentOn) {
            const spent = link.spentOn
            const target =
                spent.count > 1
                    ? `${spent.count} پرداخت`
                    : `«${spent.titles[0]}»`
            // The payments sit *above* this row — the list is newest first —
            // so a basket of three is three rows, not one row of the total.
            const above =
                spent.count > 1
                    ? `${spent.count} ردیف «−» بالای همین ردیف روی‌هم ${toman(
                          spent.total
                      )} می‌شوند`
                    : `ردیف «−${spent.total.toLocaleString()} تومان» بالای همین ردیف همان برداشت است`
            // Only a basket that used the whole top-up leaves the balance
            // where it was; whatever was left over went into the wallet.
            const rest = amount - spent.total
            const howMuch =
                rest > 0 ? `${toman(spent.total)} از آن همان لحظه` : 'همان لحظه'
            const tail =
                rest > 0
                    ? 'باقیِ این شارژ به موجودی کیف پول شما اضافه شد.'
                    : 'پس یک بار پول داده‌اید و موجودی کیف پول شما دست‌نخورده مانده است.'
            return `${lead} ${cameFrom} و ${howMuch} برای ${target} از کیف پول برداشت شد. ${above}، نه پرداختی دوباره؛ ${tail}`
        }
        return `${lead} ${cameFrom} و همان‌جا مانده است. تا خرجش نکنید موجودی کیف پول شماست و می‌توانید در هر صندوقی با آن پرداخت کنید.`
    }

    if (view.kind === 'wallet_out') {
        return `${lead} ${toman(
            amount
        )} از کیف پول همیان شما به حساب بانکی خودتان برگشت. این پول از سرمایهٔ شما در صندوق‌ها کم نمی‌کند؛ فقط از کیف پول به حساب شما رفته است.`
    }

    if (view.kind === 'in') {
        const type = item.moneypool_data?.type ?? ''
        const tail =
            type === LOAN || item.ctx_type === CTX_CYCLE
                ? ' این مبلغ وام است و در قسط‌های تعیین‌شده بازپرداخت می‌شود.'
                : type === CUSTOM_CASHOUT
                ? ' این برداشت از سرمایهٔ شما در صندوق است.'
                : ''
        return `${lead} ${toman(
            amount
        )} از «${box}» به حساب بانکی شما واریز شد.${tail}`
    }

    if (view.kind === 'out') {
        const destination =
            item.destination === HAMYAN_PLAN
                ? 'به همیان پرداخت شد'
                : `به «${box}» رفت`
        let body: string
        if (item.source === WALLET && link?.fundedBy) {
            // The top-up is older, so it is *below* this row, and one top-up
            // can cover a whole basket: «همین مبلغ» is only true when it
            // paid for this row alone.
            const charge = link.fundedBy.amount
            const where = `ردیف «+${charge.toLocaleString()} تومان» پایین‌تر در همین فهرست`
            const came =
                charge === chargedTotal(item)
                    ? `همین مبلغ لحظه‌ای پیش با درگاه بانکی به کیف پول آمده بود — ${where} — پس این دو ردیف با هم یک پرداخت‌اند، نه دو پرداخت.`
                    : `پولش لحظه‌ای پیش با شارژی به مبلغ ${toman(
                          charge
                      )} به کیف پول آمده بود — ${where} — که چند پرداخت را با هم پوشاند؛ پس این ردیف پرداختی جدا از آن شارژ نیست.`
            body = `${toman(
                amount
            )} از کیف پول همیان شما برداشت شد و ${paidFor}${destination}. ${came}`
        } else if (item.source === WALLET) {
            body = `${toman(
                amount
            )} از موجودی کیف پول همیان شما کم شد و ${paidFor}${destination}.`
        } else if (item.source === GATEWAY) {
            body = `${toman(
                amount
            )} با درگاه بانکی از حساب شما پرداخت شد و ${paidFor}${destination}.`
        } else if (item.source === CASH) {
            body = `${toman(
                amount
            )} را نقدی پرداخت کرده‌اید و مدیر صندوق آن را ${paidFor}در «${box}» ثبت کرده است.`
        } else {
            body = `${toman(amount)} ${paidFor}${destination}.`
        }
        const withCommission = commission
            ? ` کارمزد ${toman(
                  commission
              )} هم روی آن حساب شده، پس در کل ${toman(
                  chargedTotal(item)
              )} پرداخت کرده‌اید.`
            : ''
        return `${lead} ${body}${withCommission}`
    }

    // A move: the row is in the member's list because they were on one end of
    // it, but their own pocket was not that end.
    const payer = trimmed(item.payer_name)
    const receiver = trimmed(item.receiver_name)
    if (
        BOX_SIDES.includes(item.source) &&
        BOX_SIDES.includes(item.destination)
    ) {
        return `پول خودِ «${box}» از «موجودی نزد همیان» به حساب بانکی صندوق منتقل شده است. این ردیف به حساب شخصی شما کاری ندارد.`
    }
    if (item.destination === MEMBER_BANK_ACCOUNT) {
        const to = receiver ? `«${receiver}»` : 'یکی از اعضا'
        return `${toman(
            amount
        )} از «${box}» به حساب بانکی ${to} پرداخت شده و شما ثبت‌کنندهٔ آن بوده‌اید. از حساب شخصی شما چیزی کم نشده است.`
    }
    if (item.payer !== me && payer) {
        return `${toman(
            amount
        )} را «${payer}» ${paidFor}برای شما به «${box}» پرداخت کرده است. این مبلغ به سرمایهٔ شما در صندوق اضافه می‌شود ولی از جیب شما کم نشده است.`
    }
    return 'این ردیف نه به حساب شما اضافه کرده و نه از آن کم کرده است؛ در فهرست شما هست چون یک سرِ آن به شما مربوط بوده.'
}

// Iran dropped daylight saving in 1401, so Tehran is a fixed +03:30. The same
// constant as the fund tab's `days.ts`.
const TEHRAN_UTC_OFFSET_MINUTES = 210

/**
 * When it happened, on Tehran's clock. `state_time` is when the payment
 * settled — the moment the member saw it go through — and it arrives as
 * «2026-09-21 18:15:02+03:30»; `created` is the fallback for a row that never
 * got one.
 */
export function tehranMoment(
    item: Pick<IDashboardTransactionResult, 'state_time' | 'created'>
): moment.Moment | null {
    for (const stamp of [item.state_time, item.created]) {
        if (!stamp) continue
        const parsed = moment(stamp.replace(' ', 'T'), moment.ISO_8601)
        if (parsed.isValid()) return parsed.utcOffset(TEHRAN_UTC_OFFSET_MINUTES)
    }
    return null
}

/** «1405-06-30», the key `describeDay` reads — '' when there is no date. */
export function jalaliDayKey(
    item: Pick<IDashboardTransactionResult, 'state_time' | 'created'>
): string {
    const at = tehranMoment(item)
    return at ? at.format('jYYYY-jMM-jDD') : ''
}

/** «۱۸:۱۵», or «۱۸:۱۵:۰۲» for the details sheet. */
export function clockTime(
    item: Pick<IDashboardTransactionResult, 'state_time' | 'created'>,
    withSeconds = false
): string {
    const at = tehranMoment(item)
    return at ? at.format(withSeconds ? 'HH:mm:ss' : 'HH:mm') : ''
}

export interface ITransactionDay<T> {
    key: string
    rows: T[]
}

/**
 * Runs of the same day, in the order the server sent them. Runs, not buckets:
 * the list is newest-first by id, and re-sorting it here would quietly
 * disagree with the order it pages in.
 */
export function runsByDay<T extends IDashboardTransactionResult>(
    rows: T[]
): ITransactionDay<T>[] {
    const days: ITransactionDay<T>[] = []
    rows.forEach((row) => {
        const key = jalaliDayKey(row)
        const last = days[days.length - 1]
        if (last && last.key === key) last.rows.push(row)
        else days.push({ key, rows: [row] })
    })
    return days
}

// --- «بازهٔ زمانی» -----------------------------------------------------------

export type TPeriod =
    | ''
    | 'this_month'
    | 'last_month'
    | 'three_months'
    | 'this_year'

export const PERIOD_OPTIONS: { key: TPeriod; label: string; aria?: string }[] =
    [
        { key: '', label: 'همه', aria: 'همهٔ تراکنش‌ها' },
        { key: 'this_month', label: 'این ماه' },
        { key: 'last_month', label: 'ماه گذشته' },
        { key: 'three_months', label: 'سه ماه اخیر' },
        { key: 'this_year', label: 'امسال' },
    ]

export interface IPeriodRange {
    /** 'jYYYY/jMM/jDD', both ends included */
    from: string
    to: string
}

const RANGE_FORMAT = 'jYYYY/jMM/jDD'

function tehranToday(now: Date) {
    return moment(now).utcOffset(TEHRAN_UTC_OFFSET_MINUTES)
}

/**
 * The two Jalali days a period covers, both included. The server takes them
 * as `date=1405/06/01,1405/06/31` and reads the second one as the whole day.
 */
export function periodRange(
    period: TPeriod,
    now: Date = new Date()
): IPeriodRange | null {
    if (period === '') return null
    const today = tehranToday(now)
    const format = (at: moment.Moment) => at.format(RANGE_FORMAT)
    switch (period) {
        case 'this_month':
            return {
                from: format(today.clone().startOf('jMonth')),
                to: format(today),
            }
        case 'last_month': {
            const lastMonth = today.clone().startOf('jMonth').subtract(1, 'day')
            return {
                from: format(lastMonth.clone().startOf('jMonth')),
                to: format(lastMonth),
            }
        }
        case 'three_months':
            return {
                from: format(today.clone().subtract(3, 'jMonth').add(1, 'day')),
                to: format(today),
            }
        case 'this_year':
            return {
                from: format(today.clone().startOf('jYear')),
                to: format(today),
            }
        default:
            return null
    }
}

/** The `date=` the server expects, or undefined for «همه». */
export function periodParam(
    period: TPeriod,
    now: Date = new Date()
): string | undefined {
    const range = periodRange(period, now)
    return range ? `${range.from},${range.to}` : undefined
}

/**
 * «۱ شهریور تا ۲۶ شهریور ۱۴۰۵» — the year said once when both ends share it,
 * which every period but «سه ماه اخیر» in spring does.
 */
export function periodRangeLabel(range: IPeriodRange): string {
    const from = moment(range.from, RANGE_FORMAT)
    const to = moment(range.to, RANGE_FORMAT)
    if (!from.isValid() || !to.isValid()) return ''
    const day = (at: moment.Moment) =>
        `${at.jDate()} ${DigitToMonth[at.jMonth() + 1]}`
    return from.jYear() === to.jYear()
        ? `${day(from)} تا ${day(to)} ${to.jYear()}`
        : `${day(from)} ${from.jYear()} تا ${day(to)} ${to.jYear()}`
}

// --- the period's totals -----------------------------------------------------

export interface IPeriodTotals {
    received: number
    paid: number
    wallet: number
    count: number
}

/**
 * What came in, what went out and what is sitting in the wallet, over the rows
 * given. «پرداختی» counts what left the member's pocket — the commission
 * included — because that is the figure a bank statement will show them.
 *
 * A top-up that was handed straight on to a box is *not* counted as a top-up:
 * the payment under it is already in «پرداختی», and counting both would tell
 * the member they spent the same money twice.
 */
export function periodTotals(
    rows: IDashboardTransactionResult[],
    me: number | null,
    links?: Map<number, IWalletLink>
): IPeriodTotals {
    return rows.reduce<IPeriodTotals>(
        (totals, row) => {
            const kind = kindOf(row, me)
            if (kind === 'in') totals.received += row.amount ?? 0
            else if (kind === 'out') totals.paid += chargedTotal(row)
            else if (kind === 'wallet_in' && !links?.get(row.pk)?.spentOn)
                totals.wallet += row.amount ?? 0
            totals.count += 1
            return totals
        },
        { received: 0, paid: 0, wallet: 0, count: 0 }
    )
}
